import { MinusCircleIcon } from "@heroicons/react/24/outline";
import { cva } from "class-variance-authority";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ActiveMeetingType } from "src/pages/DashboardPage/mytasks/meetingsPage/type";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import { usePagination } from "../../../hooks/usePagination";
import { TableColumnWithIcon } from "../../../pages/DashboardPage/acadia/mock";
import { EmptyList } from "../../EmptyList";
import Loading from "../../Loading";
import SortIcon from "../../ui-icons/table/SortIcon";
import Pagination from "../../ui/table/Pagination";
import { TableHeaderColumnProps } from "../../ui/table/Table";

type TableHeadType = "Title" | "Location" | "Department" | "Role";

const tableHeaderContent: Record<TableHeadType, TableColumnWithIcon> = {
  Title: {
    title: "Название совещания",
    sort: true,
    style: { width: "40%" },
  },
  Location: {
    title: "Локация",
    sort: false,
    style: { width: "30%" },
  },
  Department: {
    title: "Отдел",
    sort: false,
    style: { width: "20%" },
  },
  Role: {
    title: "Роль",
    sort: false,
    style: { width: "10%" },
  },
};

function ActiveMeetingsTable({
  list,
  loading,
  activeMeeting,
}: {
  list: (Record<TableHeadType, any> & { ID: number })[];
  loading: boolean;
  activeMeeting: ActiveMeetingType;
}) {
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("default");

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return list;
    }

    const sortedData = [...list];

    const [locales, options]: [Intl.LocalesArgument, Intl.CollatorOptions] = [["ru", "en"], { sensitivity: "base" }];

    sortedData.sort((a, b) => {
      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB, locales, options)
          : valueB.localeCompare(valueA, locales, options);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, list]) as (Record<TableHeadType, any> & { ID: number })[];

  const [page, setPage] = useState(1);
  const limit = 10;

  const {
    numberOfPages,
    pageItems,
    length: activeMeetingsItemsCount,
  } = usePagination(sortedTableBodyContent, limit, page);

  function onChangePage(page: number) {
    setPage(page);
  }

  useEffect(() => {
    onChangePage(1);
  }, [list]);

  return (
    <FlexRow>
      <FlexCol className="w-full">
        <TableList
          loading={loading}
          head={tableHeaderContent}
          body={pageItems}
          sort={handleSort}
          sortDirection={sortDirection}
          sortedField={sortedField}
          activeMeeting={activeMeeting}
        />
        {activeMeetingsItemsCount > limit && (
          <Pagination numberOfPages={numberOfPages} page={page} onChange={onChangePage} />
        )}
      </FlexCol>
    </FlexRow>
  );
}

export default ActiveMeetingsTable;

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  loading,
  activeMeeting,
}: {
  head: Record<keyof typeof tableHeaderContent, TableColumnWithIcon>;
  body: (Record<TableHeadType, any> & { ID: number })[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof typeof tableHeaderContent | null;
  sortDirection: "asc" | "desc" | "default";
  loading: boolean;
  activeMeeting: ActiveMeetingType;
}) {
  const [query, setQuery] = useSearchParams();

  function openCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("meetingCardMobile", id.toString());

    setQuery(newSearchParams);
  }

  return (
    <FlexRow className="bg-white rounded-lg table-custom shadow-custom">
      <FlexCol className="divide-y grow">
        {!loading ? (
          Array.isArray(body) && body.length > 0 ? (
            body.map((item) => (
              <FlexCol
                className="p-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 cursor-pointer"
                key={item.ID}
                onClick={() => openCard(item.ID)}
              >
                <FlexCol className="break-words text-darkblue mb-2">
                  {item.Title}
                </FlexCol>
                <FlexCol className="break-words text-[#637381] mb-1">
                  {item.Location}
                </FlexCol>
                <FlexCol className="break-words text-[#637381]">
                  {item.Department}
                </FlexCol>
              </FlexCol>
            ))
          ) : (
            <EmptyList
              icon={<MinusCircleIcon width={24} height={24} color="#637381" />}
              title={`Нет ${activeMeeting === "active" ? "активных" : "архивных"} совещаний`}
            />
          )
        ) : (
          <div className="pt-4">
            <Loading />
          </div>
        )}
      </FlexCol>
    </FlexRow>
  );
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="items-center gap-x-2">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium text-black whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

const criticalItemStyle = cva("block h-2 w-2 rounded-full", {
  variants: {
    active: {
      true: "bg-[#BA0C2F]",
      false: "bg-[#8899A8]",
    },
  },
  defaultVariants: {
    active: false,
  },
});

export function CriticallyListItem({ item }: { item: boolean }) {
  return (
    <FlexRow className="items-center space-x-1">
      <FlexCol>
        <span className={twMerge(criticalItemStyle({ active: item }))}></span>
      </FlexCol>
      <FlexCol>{item ? "" : "Не "}Критичный</FlexCol>
    </FlexRow>
  );
}
